import React, { FC, useMemo } from "react";

import { Pojo } from "types/Galaxy";
import { findAll, findView } from "api";
import { GSBuilder, initRsqlCriteria } from "utils/query.utils";
import { useDirtyGalaxy } from "layout/Dirty";
import { FilterBar } from "types/Search";
import { useQuery } from "@tanstack/react-query";

export interface ProcessusContextProps {
  sjmoCode: string;
  tableName: string;
  navigationUrl?: string;
  selected?: Pojo[];
  arboCtrlKey?: string;
  dtFilter?: string;
  dtFilterBar?: FilterBar;
  dtTableName?: string;
  selectAll?: boolean;
  selectAllType?: "DATATABLE" | "INTERACTIVE_REPORT";
  ctrlKey?: string;
  isDirty(): boolean;
  onAfterSaveProcess?(): void;
}

export const ProcessusContext = React.createContext<ProcessusContextProps | null>(null);

type SelectedProps = string | Pojo | string[] | Pojo[];

export const ProcessusProvider: FC<
  React.PropsWithChildren<{
    sjmoCode: string;
    tableName: string;
    selected?: SelectedProps;
    navigationUrl?: string;
    arboCtrlKey?: string;
    ctrlKey?: string;
    entityFromView?: boolean;
    onAfterSaveProcess?(): void;
    dtFilter?: string;
    dtFilterBar?: FilterBar;
    dtTableName?: string;
    selectAll?: boolean;
    selectAllType?: "DATATABLE" | "INTERACTIVE_REPORT";
  }>
> = ({ children, selected, ...props }) => {
  const selectedEntities = useMemo(() => {
    if (selected == null || selected === undefined) return undefined;
    else if (Array.isArray(selected)) return selected;
    else return [selected];
  }, [selected]);

  const useInternalEntities = useMemo(() => {
    return selectedEntities?.every((it) => typeof it === "string") ?? false;
  }, [selectedEntities]);

  const { getDirty } = useDirtyGalaxy();

  const rsqlFilter = useMemo(() => {
    if (!useInternalEntities) return null;
    if (selectedEntities === undefined || selectedEntities.length === 0) return null;

    const filter = GSBuilder.toFilter(GSBuilder.Comparison("id", "OPER_IN", selectedEntities));
    const rsql = initRsqlCriteria();
    rsql.filters.and(filter);
    return rsql.build();
  }, [selectedEntities, useInternalEntities]);

  const { data: fetchedEntities = [], isFetching } = useQuery<Pojo[]>({
    queryKey: ["processusEntities", props.tableName, rsqlFilter],
    queryFn: async () => {
      if (!rsqlFilter) {
        return [];
      }

      try {
        if (props.entityFromView) {
          const res = await findView({
            tableName: props.tableName,
            filter: rsqlFilter,
            size: false,
          });
          return res.data.data;
        } else {
          const res = await findAll({
            tableName: props.tableName,
            filter: rsqlFilter,
            size: false,
          });
          return res.data.data;
        }
      } catch {
        return [];
      }
    },
    enabled: !!rsqlFilter,
  });

  const enhancedChildren = useMemo(() => {
    if (!React.isValidElement(children)) {
      return children;
    }

    const disabledProp = (children as any).props?.disabled;

    return React.cloneElement(children, {
      disabled: disabledProp !== undefined ? disabledProp : isFetching && useInternalEntities,
    });
  }, [children, isFetching, useInternalEntities]);

  const contextValue = useMemo(
    () => ({
      ...props,
      isDirty: () => getDirty(props.sjmoCode),
      selected: useInternalEntities ? fetchedEntities : (selectedEntities as Pojo[]),
    }),
    [fetchedEntities, getDirty, props, selectedEntities, useInternalEntities],
  );

  return (
    <ProcessusContext.Provider value={contextValue}>{enhancedChildren}</ProcessusContext.Provider>
  );
};
